<template>
  <section
    ref="$block"
    class="osk-h-selection-banner-block"
  >
    <div class="osk-wrapper">
      <h2
        v-if="data.title"
        class="osk-h-selection-banner-block__title"
      >
        <span
          class="osk-h-selection-banner-block__title-text"
        >{{ data.title }}</span>
      </h2>

      <div class="osk-h-selection-banner-block__content">
        <div class="osk-h-selection-banner-block__image">
          <img
            :src="data.imagePath"
            :alt="data.title"
            class="osk-h-selection-banner-block__card-image"
          >

          <div class="osk-h-selection-banner-block__image-content">
            <span class="osk-h-selection-banner-block__image-content-title">
              {{ data.title }}
            </span>

            <span class="osk-h-selection-banner-block__image-content-text">
              {{ data.additionalData?.description }}
            </span>

            <UikitButton
              v-if="link"
              class="osk-h-selection-banner-block__image-button"
              outline
              color="white"
              size="s"
              @click="goToNextPage"
            >
              {{ $t('components.home.blocks.hSelectionWithBanner.allProducts') }}
            </UikitButton>
          </div>
        </div>

        <UikitSwiper
          v-if="slides.length"
          ref="$swiper"
          off
          navigation
          :css-mode="!isDesktop"
          :breakpoints="sliderBreakpoints"
          class="osk-h-selection-banner-block__swiper"
          @slide-change="handleSlideChange"
        >
          <UikitSwiperSlide
            v-for="(slide, index) in slides"
            :key="index"
          >
            <template v-if="Array.isArray(slide)">
              <UikitProductCard
                v-for="(product, idx) in slide"
                :key="idx"
                :index="index"
                :product="product"
                :is-seller-hidden="isSellerHidden"
                :small="isDesktop || laptopM"
                use-swiper-analytics
                :analytics="analytics"
                @product-click="handleProductClick"
              />
            </template>

            <UikitProductCard
              v-else
              :index="index"
              :product="slide"
              :is-seller-hidden="isSellerHidden"
            />
          </UikitSwiperSlide>
        </UikitSwiper>
      </div>

      <div
        v-if="link"
        class="osk-h-selection-banner-block__footer"
      >
        <UikitButton
          class="osk-h-selection-banner-block__button"
          outline
          size="m"
          full-width
          @click="goToNextPage"
        >
          {{ $t('components.home.blocks.hSelectionWithBanner.seeAllProducts') }}
        </UikitButton>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import chunk from 'lodash.chunk';

import { PrimaryContentDTO, PrimaryPageProductDTO } from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const sliderBreakpoints = {
  0: {
    slidesPerView: 1.50,
    slidesPerGroup: 1,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  478: {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  580: {
    slidesPerView: 2.5,
    slidesPerGroup: 2,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  768: {
    slidesPerView: 1,
    slidesPerGroup: 1,
    spaceBetween: 24,
    navigation: { enabled: true },
  },
  1024: {
    slidesPerView: 1,
    slidesPerGroup: 1,
    spaceBetween: 24,
    navigation: { enabled: true },
  },
};

const props = defineProps<{
  data: PrimaryContentDTO
}>();

const $block = ref<ComponentPublicInstance>();
const $swiper = ref();

const { isDesktop, laptopM, isMounted } = useUiBreakpoints();
const { $addEvent } = useNuxtApp();

const contentList = computed(() => (props.data.contentList ?? []) as PrimaryPageProductDTO[]);

const slides = computed(() => {
  if (!isMounted.value || !(isDesktop.value || laptopM.value)) {
    return contentList.value;
  }

  const desktopList: PrimaryPageProductDTO[][] = chunk(contentList.value, 3);

  return desktopList;
});

const allIds = computed(() => {
  const all = contentList.value.map((i) => i.id!);

  if (!isMounted.value || !(isDesktop.value || laptopM.value)) {
    return all;
  }

  const allChunked: number[][] = chunk(all, 3);

  return allChunked;
});

const analytics = computed(() => ({
  chapter: props.data.contentType,
  chapter_index: props.data.chapterIndex,
  segment_id: props.data.segmentId,
  id_chapter: props.data.id,
}));

const { event, clickEvent } = useSwiperAnalytics(
  $swiper,
  $block,
  allIds,
  analytics.value,
  sliderBreakpoints,
);

function handleProductClick({ productId, index, value }: { productId?: number, index?: number, value?: string }) {
  clickEvent({
    id_item: String(productId),
    index,
    item: 'product',
    value,
  });
}

function handleSlideChange() {
  event();
}

const isSellerHidden = computed(() => (props.data.additionalData?.isSellerHidden ?? false));

const link = computed(() => genSPALink(props.data.additionalData?.jumpLink));

async function goToNextPage() {
  if (!link.value) return;

  $addEvent(AnalyticsEvents.ShowAllClick, analytics.value);

  await navigateTo(link.value);
}
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-h-selection-banner-block {
  padding-bottom: 42px;

  @include media-query(lg-and-up) {
    padding-bottom: 82px;
    margin-bottom: 0;
  }

  .osk-wrapper {
    padding: 0;

    @include media-query(lg-and-up) {
      padding: 0 16px;
    }
  }

  &__title {
    display: none;

    @include media-query(md-and-up) {
      @include overflow-ellipsis;
      @include font-style($font-size-heading, normal, $font-weight-bold);
      display: block;
      text-transform: uppercase;
      text-align: center;
      padding: 0 80px 34px;
      position: relative;
    }
  }

  &__content {
    @include media-query(md-and-up) {
      display: flex;
    }
  }

  &__swiper {
    @include media-query(md-and-up) {
      width: 45%;
    }

    @include media-query(lg-and-up) {
      width: 100%;
      max-width: 446px;
    }
  }

  &__image {
    position: relative;
    margin-bottom: $indent-medium;
    height: 300px;

    @include media-query(md-and-up) {
      max-width: 706px;
      width: 55%;
      flex-shrink: 0;
      padding-right: 24px;
      margin-bottom: 0;
      height: 540px;
    }

    @include media-query(lg-and-up) {
      padding-right: 0;
      margin-right: 48px;
      width: 100%;
      flex-shrink: 0;
    }

    &:before {
      content: '';
      background: linear-gradient(0deg, #111 0%, rgba(17, 17, 17, 0.99) 6.6%, rgba(17, 17, 17, 0.96) 13.19%,
        rgba(17, 17, 17, 0.92) 19.79%, rgba(17, 17, 17, 0.85) 26.38%, rgba(17, 17, 17, 0.77) 32.98%,
        rgba(17, 17, 17, 0.67) 39.57%, rgba(17, 17, 17, 0.56) 46.17%, rgba(17, 17, 17, 0.44) 52.77%,
        rgba(17, 17, 17, 0.33) 59.36%, rgba(17, 17, 17, 0.23) 65.96%, rgba(17, 17, 17, 0.15) 72.55%,
        rgba(17, 17, 17, 0.08) 79.15%, rgba(17, 17, 17, 0.04) 85.74%, rgba(17, 17, 17, 0.01) 92.34%,
        rgba(17, 17, 17, 0.00) 98.94%);
      position: absolute;
      top:0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }

  &__card-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 2px;
  }

  &__image-content {
    position: absolute;
    bottom: 16px;
    left: 16px;
    right: 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    color: #fff;
    z-index: 1;

    @include media-query(md-and-up) {
      bottom: 32px;
      left: 32px;
      right: auto;
      padding-right: 32px;
    }
  }

  &__image-content-title {
    @include line-clamp;
    @include font-style($font-size-subheading, normal, $font-weight-bold);
    margin-bottom: $indent-xsmall;

    @include media-query(md-and-up) {
      display: none;
    }
  }

  &__image-content-text {
    @include line-clamp(3);
    @include font-style($font-size-caption, normal, $font-weight-medium);
  }

  &__image-button {
    text-transform: uppercase;
    display: none;

    @include media-query(md-and-up) {
      display: block;
      margin-top: 24px;
    }
  }

  @include swiper-section-nav;

  .swiper-slide {
    padding: 0 16px;
    margin-right: -20px;

    @include media-query(lg-and-up) {
      padding: 0;
      margin: 0;
    }
  }

  .osk-swiper_off {
    .swiper-wrapper {
      overflow: visible;
      justify-content: space-between;
    }

    .swiper-slide {
      width: 100% !important;
      max-width: 282px;
      margin-right: 24px;
      flex-shrink: 0;
    }
  }

  &__footer {
    padding: 0 $indent-medium;
    margin-top: $indent-large;

    @include media-query(md-and-up) {
      display: none;
    }
  }
}
</style>
